import React, { useEffect, useMemo, useState } from 'react';
import { KeyRound, LoaderCircle, Pencil, Plus, ShieldCheck, Trash2, UserRoundPlus, Users, X } from 'lucide-react';
import { useSession } from '../context/SessionContext';
import { getSessionWorkspace, getSessionWorkspaceMembers, revokeSessionWorkspaceMember, saveSessionWorkspaceMember } from '../services/api';
import { useAppDialogs } from '../context/AppDialogContext';

type WorkspaceMember = {
  id: number;
  phone_number: string;
  display_name: string;
  role: string;
  permissions: string[];
  is_active: boolean;
};

type Workspace = {
  id: number;
  name: string;
  phone_number: string;
};

type ToastApi = {
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
};

const permissionGroups: { title: string; permissions: [string, string][] }[] = [
  {
    title: 'Panel y conversaciones',
    permissions: [
      ['dashboard.read', 'Ver el panel'],
      ['chats.read', 'Ver chats y clientes'],
    ],
  },
  {
    title: 'Reglas y automatizaciones',
    permissions: [
      ['rules.read', 'Consultar reglas'],
      ['rules.create', 'Crear reglas'],
      ['rules.update', 'Editar reglas'],
      ['rules.delete', 'Eliminar reglas'],
    ],
  },
  {
    title: 'Productos y cursos',
    permissions: [
      ['products.read', 'Consultar productos'],
      ['products.create', 'Crear productos'],
      ['products.update', 'Editar productos'],
      ['products.delete', 'Eliminar productos'],
      ['courses.read', 'Consultar cursos'],
      ['courses.create', 'Crear cursos'],
      ['courses.update', 'Editar cursos'],
      ['courses.delete', 'Eliminar cursos'],
    ],
  },
  {
    title: 'Menús y memoria',
    permissions: [
      ['menus.read', 'Consultar menús'],
      ['menus.create', 'Crear menús'],
      ['menus.update', 'Editar menús'],
      ['menus.delete', 'Eliminar menús'],
      ['memory.read', 'Consultar memoria'],
      ['memory.update', 'Editar memoria'],
      ['memory.delete', 'Eliminar memoria'],
      ['knowledge.read', 'Consultar conocimiento del espacio'],
      ['knowledge.create', 'Añadir datos al conocimiento'],
      ['knowledge.update', 'Editar datos de conocimiento'],
      ['knowledge.delete', 'Eliminar datos de conocimiento'],
      ['data_models.read', 'Consultar tablas dinámicas'],
      ['data_models.create', 'Crear tablas y columnas'],
      ['data_models.update', 'Editar tablas y columnas'],
      ['data_models.delete', 'Eliminar tablas dinámicas'],
      ['data_records.read', 'Consultar registros dinámicos'],
      ['data_records.create', 'Crear registros dinámicos'],
      ['data_records.update', 'Editar registros dinámicos'],
      ['data_records.delete', 'Eliminar registros dinámicos'],
    ],
  },
  {
    title: 'Pagos, equipo y configuración',
    permissions: [
      ['payments.read', 'Consultar pagos'],
      ['payments.create', 'Registrar pagos'],
      ['payments.update', 'Gestionar pagos'],
      ['members.read', 'Consultar números autorizados'],
      ['members.invite', 'Autorizar números'],
      ['members.update', 'Cambiar permisos'],
      ['members.remove', 'Revocar permisos'],
      ['settings.manage', 'Administrar la sesión'],
    ],
  },
];

const allPermissions = permissionGroups.flatMap((group) => group.permissions.map(([key]) => key));

const rolePresets: Record<string, { label: string; description: string; permissions: string[] }> = {
  administrator: {
    label: 'Administrador',
    description: 'Puede gestionar el contenido y la operación de esta sesión.',
    permissions: allPermissions.filter((permission) => !permission.startsWith('members.')),
  },
  content: {
    label: 'Editor de contenido',
    description: 'Gestiona productos, cursos y reglas de respuesta.',
    permissions: ['dashboard.read', 'chats.read', 'rules.read', 'rules.create', 'rules.update', 'products.read', 'products.create', 'products.update', 'courses.read', 'courses.create', 'courses.update', 'knowledge.read', 'knowledge.create', 'knowledge.update', 'data_models.read', 'data_models.create', 'data_models.update', 'data_records.read', 'data_records.create', 'data_records.update'],
  },
  automation: {
    label: 'Gestor de automatizaciones',
    description: 'Consulta y mantiene reglas de respuesta.',
    permissions: ['dashboard.read', 'chats.read', 'rules.read', 'rules.create', 'rules.update', 'rules.delete'],
  },
  payments: {
    label: 'Operador de pagos',
    description: 'Consulta pagos y actualiza su estado.',
    permissions: ['dashboard.read', 'chats.read', 'payments.read', 'payments.update'],
  },
  viewer: {
    label: 'Solo consulta',
    description: 'Puede revisar el panel, los chats y el contenido.',
    permissions: ['dashboard.read', 'chats.read', 'rules.read', 'products.read', 'courses.read', 'payments.read'],
  },
  custom: {
    label: 'Personalizado',
    description: 'Elige exactamente lo que puede hacer este número.',
    permissions: [],
  },
};

interface SessionAccessManagerProps {
  toast?: ToastApi;
}

export const SessionAccessManager: React.FC<SessionAccessManagerProps> = ({ toast }) => {
  const { confirmAction } = useAppDialogs();
  const { selectedSessionId, selectedSession } = useSession();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('content');
  const [permissions, setPermissions] = useState<string[]>(rolePresets.content.permissions);
  const [editingPhone, setEditingPhone] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedPhone = useMemo(() => selectedSession?.phoneNumber || '', [selectedSession?.phoneNumber]);

  const loadWorkspace = async () => {
    if (!selectedSessionId) {
      setWorkspace(null);
      setMembers([]);
      return;
    }
    setLoading(true);
    try {
      const [workspaceResponse, membersResponse] = await Promise.all([
        getSessionWorkspace(),
        getSessionWorkspaceMembers(),
      ]);
      setWorkspace(workspaceResponse.workspace || null);
      setMembers(membersResponse.members || []);
    } catch (error: any) {
      setWorkspace(null);
      setMembers([]);
      toast?.error(error?.response?.data?.error || 'No se pudo cargar el equipo de esta sesión.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPhoneNumber('');
    setDisplayName('');
    setEditingPhone(null);
    void loadWorkspace();
  }, [selectedSessionId]);

  const applyRole = (nextRole: string) => {
    setRole(nextRole);
    setPermissions(rolePresets[nextRole]?.permissions || []);
  };

  const togglePermission = (permission: string) => {
    setRole('custom');
    setPermissions((current) => current.includes(permission)
      ? current.filter((item) => item !== permission)
      : [...current, permission]);
  };

  const startEditing = (member: WorkspaceMember) => {
    setEditingPhone(member.phone_number);
    setPhoneNumber(member.phone_number);
    setDisplayName(member.display_name || '');
    setRole(rolePresets[member.role] ? member.role : 'custom');
    setPermissions(member.permissions || []);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setEditingPhone(null);
    setPhoneNumber('');
    setDisplayName('');
    applyRole('content');
  };

  const saveMember = async (event: React.FormEvent) => {
    event.preventDefault();
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 8 || cleanPhone.length > 15) {
      toast?.error('Escribe el número con código de país, solo con dígitos.');
      return;
    }
    if (!permissions.length) {
      toast?.error('Selecciona al menos un permiso.');
      return;
    }
    setSaving(true);
    try {
      const response = await saveSessionWorkspaceMember({
        phoneNumber: cleanPhone,
        displayName: displayName.trim(),
        role,
        permissions,
      });
      setMembers(response.members || []);
      resetForm();
      toast?.success(editingPhone ? 'Permisos actualizados.' : 'Número autorizado para esta sesión.');
    } catch (error: any) {
      toast?.error(error?.response?.data?.error || 'No se pudieron guardar los permisos.');
    } finally {
      setSaving(false);
    }
  };

  const revokeMember = async (member: WorkspaceMember) => {
    const label = member.display_name || `+${member.phone_number}`;
    if (!(await confirmAction({ title: 'Revocar acceso', message: `¿Revocar el acceso de ${label} a esta sesión?`, type: 'danger', confirmText: 'Revocar' }))) return;
    try {
      await revokeSessionWorkspaceMember(member.phone_number);
      setMembers((current) => current.filter((item) => item.phone_number !== member.phone_number));
      if (editingPhone === member.phone_number) resetForm();
      toast?.success('Acceso revocado.');
    } catch (error: any) {
      toast?.error(error?.response?.data?.error || 'No se pudo revocar el acceso.');
    }
  };

  if (!selectedSessionId) {
    return (
      <div className="w-full min-w-0 rounded-2xl border border-theme bg-theme-card p-8 text-center">
        <Users className="mx-auto mb-3 text-theme-muted" size={32} />
        <h3 className="text-lg font-semibold text-theme-main">Selecciona una sesión</h3>
        <p className="mt-1 text-sm text-theme-muted">El equipo y los permisos se configuran por cada número de WhatsApp.</p>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-6">
      <section className="overflow-hidden rounded-2xl border border-theme bg-theme-card shadow-sm">
        <div className="border-b border-theme bg-theme-base/70 px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-primary-500/10 p-2.5 text-primary-600">
              <ShieldCheck size={21} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-theme-main">Equipo de esta sesión</h3>
              <p className="mt-1 text-sm text-theme-muted">Autoriza números para que el asistente reciba instrucciones administrativas dentro de este espacio.</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="rounded-full border border-theme bg-theme-card px-3 py-1.5 font-semibold text-theme-main">
              WhatsApp: {selectedPhone ? `+${selectedPhone.replace(/^\+/, '')}` : 'Sin número'}
            </span>
            {workspace && <span className="rounded-full border border-theme bg-theme-card px-3 py-1.5 text-theme-muted">Espacio #{workspace.id}</span>}
            <span className="rounded-full bg-theme-success-soft px-3 py-1.5 text-xs font-medium text-theme-success">Propietario: tu cuenta de aplicación</span>
          </div>
        </div>

        <form onSubmit={saveMember} className="grid gap-6 p-6 xl:grid-cols-[minmax(240px,0.8fr)_minmax(0,1.2fr)]">
          <div className="space-y-4">
            <div>
              <label htmlFor="workspace-member-phone" className="mb-1.5 block text-sm font-semibold text-theme-main">Número autorizado</label>
              <input
                id="workspace-member-phone"
                type="tel"
                inputMode="tel"
                autoComplete="off"
                placeholder="51987654321"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                className="w-full rounded-xl border border-theme bg-theme-card px-3.5 py-2.5 text-sm text-theme-main outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
              <p className="mt-1.5 text-xs text-theme-muted">Incluye el código de país. No es el número del bot.</p>
            </div>
            <div>
              <label htmlFor="workspace-member-name" className="mb-1.5 block text-sm font-semibold text-theme-main">Nombre (opcional)</label>
              <input
                id="workspace-member-name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="Ej. Coordinación académica"
                className="w-full rounded-xl border border-theme bg-theme-card px-3.5 py-2.5 text-sm text-theme-main outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="workspace-member-role" className="mb-1.5 block text-sm font-semibold text-theme-main">Perfil inicial</label>
              <select
                id="workspace-member-role"
                value={role}
                onChange={(event) => applyRole(event.target.value)}
                className="w-full rounded-xl border border-theme bg-theme-card px-3.5 py-2.5 text-sm text-theme-main outline-none focus:ring-2 focus:ring-primary-500"
              >
                {Object.entries(rolePresets).map(([key, preset]) => <option key={key} value={key}>{preset.label}</option>)}
              </select>
              <p className="mt-1.5 text-xs text-theme-muted">{rolePresets[role]?.description}</p>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={saving || loading || !workspace}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? <LoaderCircle size={16} className="animate-spin" /> : editingPhone ? <ShieldCheck size={16} /> : <UserRoundPlus size={16} />}
                {saving ? 'Guardando…' : editingPhone ? 'Guardar permisos' : 'Autorizar número'}
              </button>
              {editingPhone && (
                <button type="button" onClick={resetForm} className="inline-flex items-center gap-1.5 rounded-xl border border-theme px-3 py-2.5 text-sm font-medium text-theme-muted hover:bg-theme-base">
                  <X size={15} /> Cancelar
                </button>
              )}
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-bold text-theme-main">Permisos concretos</h4>
                <p className="text-xs text-theme-muted">El asistente solo podrá ejecutar acciones autorizadas.</p>
              </div>
              <span className="rounded-full bg-theme-base px-2.5 py-1 text-xs font-semibold text-theme-muted">{permissions.length} seleccionados</span>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {permissionGroups.map((group) => (
                <fieldset key={group.title} className="rounded-xl border border-theme p-3.5">
                  <legend className="px-1 text-xs font-bold uppercase tracking-wide text-theme-muted">{group.title}</legend>
                  <div className="mt-1 space-y-2">
                    {group.permissions.map(([key, label]) => (
                      <label key={key} className="flex cursor-pointer items-center gap-2.5 text-sm text-theme-main">
                        <input
                          type="checkbox"
                          checked={permissions.includes(key)}
                          onChange={() => togglePermission(key)}
                          className="h-4 w-4 rounded border-theme text-primary-600 focus:ring-primary-500"
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
          </div>
        </form>
      </section>

      <section className="rounded-2xl border border-theme bg-theme-card p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-theme-base p-2.5 text-theme-muted"><Users size={20} /></div>
            <div>
              <h3 className="font-bold text-theme-main">Números autorizados</h3>
              <p className="text-xs text-theme-muted">Solo tienen acceso a {selectedPhone ? `+${selectedPhone.replace(/^\+/, '')}` : 'esta sesión'}.</p>
            </div>
          </div>
          <span className="rounded-full bg-theme-base px-3 py-1 text-xs font-bold text-theme-muted">{members.length}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-theme-muted"><LoaderCircle size={18} className="animate-spin" /> Cargando equipo…</div>
        ) : members.length === 0 ? (
          <div className="rounded-xl border border-dashed border-theme bg-theme-base/50 px-5 py-8 text-center">
            <KeyRound className="mx-auto mb-2 text-theme-muted" size={23} />
            <p className="text-sm font-semibold text-theme-main">Todavía no hay números delegados</p>
            <p className="mt-1 text-xs text-theme-muted">El propietario de la cuenta mantiene el control completo de esta sesión.</p>
          </div>
        ) : (
          <div className="divide-y divide-theme">
            {members.map((member) => (
              <div key={member.phone_number} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-theme-main">{member.display_name || `+${member.phone_number}`}</p>
                  <p className="mt-0.5 text-sm text-theme-muted">+{member.phone_number} · {rolePresets[member.role]?.label || 'Personalizado'}</p>
                  <p className="mt-1 text-xs text-theme-muted">{member.permissions.length} permisos</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button type="button" onClick={() => startEditing(member)} className="inline-flex items-center gap-1.5 rounded-lg border border-theme px-3 py-2 text-xs font-semibold text-theme-main hover:bg-theme-base">
                    <Pencil size={14} /> Editar
                  </button>
                  <button type="button" onClick={() => void revokeMember(member)} className="inline-flex items-center gap-1.5 rounded-lg border border-theme-danger px-3 py-2 text-xs font-semibold text-theme-danger hover:bg-theme-danger-soft">
                    <Trash2 size={14} /> Revocar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <p className="px-1 text-xs leading-relaxed text-theme-muted">
        La cuenta que vinculó el WhatsApp con el QR es la propietaria del espacio. La ejecución de acciones del asistente se conectará a estos permisos; los cambios sensibles deberán confirmarse y quedarán en el registro de actividad.
      </p>
    </div>
  );
};
